import { useState, useCallback, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import SearchBar from '../components/SearchBar';
import SearchResults from '../components/SearchResults';
import JudgmentModal from '../components/JudgmentModal';
import SearchFilters, { SearchFiltersState } from '../components/SearchFilters';
import ExportMenu from '../components/ExportMenu';
import ResultSkeleton from '../components/ResultSkeleton';
import DescribeSituationForm from '../components/DescribeSituationForm';
import SEOHead from '../components/SEOHead';
import { useSearchHistory } from '../hooks/useSearchHistory';
import api, { SearchResult, SearchResponse } from '../services/api';

type SearchType = 'semantic' | 'hybrid' | 'keyword';

const defaultFilters: SearchFiltersState = {
  courtType: '',
  dateFrom: '',
  dateTo: '',
  threshold: 0.3,
  judgmentType: '',
  judgeName: '',
  legalBase: '',
};

export default function SearchPage() {
  const { t } = useTranslation();
  // Accumulated results for "Load More" pattern
  const [allResults, setAllResults] = useState<SearchResult[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [durationMs, setDurationMs] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedJudgmentId, setSelectedJudgmentId] = useState<number | null>(null);
  const [searchType, setSearchType] = useState<SearchType>('semantic');
  const [currentQuery, setCurrentQuery] = useState('');
  const [filters, setFilters] = useState<SearchFiltersState>(defaultFilters);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [showSituationForm, setShowSituationForm] = useState(false);

  const { history, addToHistory, removeFromHistory, clearHistory } = useSearchHistory();
  const [searchParams, setSearchParams] = useSearchParams();

  const RESULTS_PER_PAGE = 10;

  // Perform search with given parameters
  const performSearch = useCallback(
    async (
      query: string,
      type: SearchType,
      offset: number,
      searchFilters: SearchFiltersState
    ): Promise<SearchResponse> => {
      const baseOptions = {
        limit: RESULTS_PER_PAGE,
        offset,
        courtType: searchFilters.courtType,
        dateFrom: searchFilters.dateFrom,
        dateTo: searchFilters.dateTo,
        judgmentType: searchFilters.judgmentType,
        judgeName: searchFilters.judgeName,
        legalBase: searchFilters.legalBase,
      };

      if (type === 'semantic') {
        return api.searchSemantic(query, {
          ...baseOptions,
          threshold: searchFilters.threshold,
        });
      } else if (type === 'keyword') {
        return api.searchKeyword(query, baseOptions);
      } else {
        return api.searchHybrid(query, {
          ...baseOptions,
          vectorWeight: 0.6,
        });
      }
    },
    []
  );

  // Initial search (resets results)
  const handleSearch = useCallback(
    async (query: string, type: SearchType, _page = 0, searchFilters?: SearchFiltersState) => {
      if (!query.trim()) {
        setError(t('search.enterQuery'));
        return;
      }

      const activeFilters = searchFilters || filters;

      setIsLoading(true);
      setError(null);
      setCurrentQuery(query);
      setSearchType(type);
      // Reset accumulated results for new search
      setAllResults([]);
      setTotalCount(0);
      setHasMore(false);

      try {
        const response = await performSearch(query, type, 0, activeFilters);

        setAllResults(response.results);
        setTotalCount(response.total);
        setDurationMs(response.durationMs);
        setHasMore(response.results.length < response.total);

        addToHistory({
          query,
          type,
          resultCount: response.total,
        });
      } catch (err) {
        setError(err instanceof Error ? err.message : t('search.searchError'));
        setAllResults([]);
        setTotalCount(0);
      } finally {
        setIsLoading(false);
      }
    },
    [filters, addToHistory, performSearch]
  );

  // Load more results (appends to existing)
  const handleLoadMore = useCallback(async () => {
    if (isLoadingMore || !hasMore) return;

    setIsLoadingMore(true);

    try {
      const response = await performSearch(currentQuery, searchType, allResults.length, filters);

      setAllResults((prev) => [...prev, ...response.results]);
      setHasMore(allResults.length + response.results.length < response.total);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('search.loadError'));
    } finally {
      setIsLoadingMore(false);
    }
  }, [isLoadingMore, hasMore, currentQuery, searchType, allResults.length, filters, performSearch]);

  // Auto-search from URL query param (e.g., /search?q=some+query)
  useEffect(() => {
    const q = searchParams.get('q');
    if (q && !currentQuery) {
      handleSearch(q, 'semantic');
      setSearchParams({}, { replace: true });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleResultClick = (result: SearchResult) => {
    setSelectedJudgmentId(result.judgmentId);
  };

  const handleHistorySelect = (query: string, type: SearchType) => {
    handleSearch(query, type, 0);
  };

  const handleApplyFilters = () => {
    if (currentQuery) {
      handleSearch(currentQuery, searchType, 0, filters);
    }
  };

  // Check if we have results to show
  const hasResults = allResults.length > 0;

  return (
    <>
      <SEOHead
        title="Wyszukiwarka orzeczeń sądowych | AnalizaPrawna"
        description="Przeszukaj ponad 500 000 orzeczeń polskich sądów administracyjnych. Wyszukiwanie semantyczne AI, filtry, eksport wyników."
        canonicalUrl="https://analizaprawna.eu/search"
      />
      <h1 className="sr-only">{t('search.srOnlyTitle')}</h1>
      {/* Situation form toggle */}
      <div className="mb-4">
        <button
          onClick={() => setShowSituationForm(!showSituationForm)}
          className="inline-flex items-center gap-2 text-sm text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 font-medium transition-colors"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
          </svg>
          {t('situationForm.searchToggle')}
          <svg
            className={`w-4 h-4 transition-transform ${showSituationForm ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>
      </div>

      {/* Situation form (collapsible) */}
      {showSituationForm && (
        <div className="mb-8 p-6 bg-slate-50 dark:bg-slate-800/50 rounded-xl border border-slate-200 dark:border-slate-700">
          <DescribeSituationForm variant="search" />
        </div>
      )}

      {/* Search section */}
      <div className="mb-6">
        <SearchBar
          onSearch={handleSearch}
          isLoading={isLoading}
          defaultType={searchType}
          defaultQuery={currentQuery}
          history={history}
          onHistorySelect={handleHistorySelect}
          onHistoryRemove={removeFromHistory}
          onHistoryClear={clearHistory}
        />
      </div>

      {/* Toolbar */}
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <SearchFilters
            filters={filters}
            onChange={setFilters}
            onApply={handleApplyFilters}
            isOpen={filtersOpen}
            onToggle={() => setFiltersOpen(!filtersOpen)}
          />
          {hasResults && (
            <span className="text-sm text-slate-500 dark:text-slate-400">
              {t('search.resultsCount', { shown: allResults.length, total: totalCount, time: (durationMs / 1000).toFixed(2) })}
            </span>
          )}
        </div>
        {hasResults && (
          <ExportMenu
            results={allResults}
            query={currentQuery}
            disabled={isLoading}
          />
        )}
      </div>

      {/* Error message */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg">
          <div className="flex items-center gap-2 text-red-700 dark:text-red-400">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span>{error}</span>
          </div>
        </div>
      )}

      {/* Loading skeleton */}
      {isLoading && <ResultSkeleton count={5} />}

      {/* Results */}
      {!isLoading && hasResults && (
        <SearchResults
          results={allResults}
          isLoading={isLoading}
          onResultClick={handleResultClick}
          onLoadMore={handleLoadMore}
          hasMore={hasMore}
          isLoadingMore={isLoadingMore}
        />
      )}

      {/* No results state */}
      {!isLoading && currentQuery && !hasResults && !error && (
        <div className="text-center py-12">
          <div className="w-16 h-16 mx-auto mb-4 bg-slate-100 dark:bg-slate-800 rounded-full flex items-center justify-center">
            <svg className="w-8 h-8 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <p className="text-slate-500 dark:text-slate-400">
            {t('search.noResults')}
          </p>
        </div>
      )}

      {/* Empty state (no search yet) */}
      {!currentQuery && !isLoading && !error && (
        <div className="text-center py-16">
          <div className="w-24 h-24 mx-auto mb-6 bg-slate-100 dark:bg-slate-800 rounded-full flex items-center justify-center">
            <svg className="w-12 h-12 text-slate-400 dark:text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-slate-700 dark:text-slate-300 mb-2">
            {t('search.emptyTitle')}
          </h2>
          <p className="text-slate-500 dark:text-slate-400 max-w-md mx-auto">
            {t('search.emptyDescription')}
          </p>
          <div className="mt-6 flex flex-wrap justify-center gap-2">
            <ExampleQuery query="odszkodowanie za wypadek drogowy" onClick={() => handleSearch('odszkodowanie za wypadek drogowy', 'semantic')} />
            <ExampleQuery query="konstytucyjność ustawy" onClick={() => handleSearch('konstytucyjność ustawy', 'semantic')} />
            <ExampleQuery query="zadośćuczynienie" onClick={() => handleSearch('zadośćuczynienie', 'semantic')} />
          </div>
        </div>
      )}

      {/* Judgment modal */}
      {selectedJudgmentId && (
        <JudgmentModal
          judgmentId={selectedJudgmentId}
          onClose={() => setSelectedJudgmentId(null)}
        />
      )}
    </>
  );
}

function ExampleQuery({ query, onClick }: { query: string; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className="px-3 py-1.5 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-600 dark:text-slate-400 text-sm rounded-full transition-colors"
    >
      {query}
    </button>
  );
}
